// src/components/UserAvatar.jsx — Shared Profile Avatar v225 (patched)
// Renders: Persona icon + Aura frame ring + Nation tag badge + optional Stickers
// Used by: Sidebar, Leaderboard, Forums, Profile, and anywhere profile pictures appear
//
// v225 patch fixes:
//  • Flag emoji → CDN image converter (Windows shows "IN"/"USA" text otherwise)
//  • Pub/sub on invalidateCache so all mounted avatars re-fetch (Sidebar/LB/Forums auto-update on equip)
//  • showStickers prop now actually renders stickers around the avatar
//  • useEquippedItems() hook lets parents (Leaderboard) read live stickers from cache
import React, { useState, useEffect } from 'react';
import { G, rarityColor, rarityGlow } from '../constants';
// P34c (2026-05-24): PRODUCTION-GRADE asset URL resolution.
// Auras stored in DB as '/store/auras/halo.svg' (relative). Backend now
// owns these files (backend/public/store/), so we resolve them through
// backend origin instead of relying on same-origin webapp/public/ serving.
import { toAbsoluteStoreUrl } from '../services/api';


// Convert flag emoji/name to actual image URL (Windows can't render flag emojis)
const FLAG_CODES = {
  india:'in', usa:'us', 'united states':'us', uk:'gb', 'united kingdom':'gb',
  japan:'jp', germany:'de', france:'fr', brazil:'br', canada:'ca', australia:'au',
  'south korea':'kr', korea:'kr', china:'cn', russia:'ru',
  italy:'it', spain:'es', mexico:'mx', netherlands:'nl', singapore:'sg', uae:'ae',
  'saudi arabia':'sa', nigeria:'ng', turkey:'tr', indonesia:'id', pakistan:'pk',
  bangladesh:'bd', philippines:'ph', vietnam:'vn', thailand:'th', malaysia:'my',
};

// Convert a flag emoji (e.g. "🇮🇳") into its ISO 2-letter country code (e.g. "IN")
// Flag emojis are 2 regional-indicator chars: 'A' = U+1F1E6 ... 'Z' = U+1F1FF.
function flagEmojiToCode(emoji) {
  if (!emoji || typeof emoji !== 'string') return null;
  const cps = [...emoji].map(c => c.codePointAt(0));
  if (cps.length < 2) return null;
  const A = 0x1F1E6;
  const c1 = cps[0] - A, c2 = cps[1] - A;
  if (c1 < 0 || c1 > 25 || c2 < 0 || c2 > 25) return null;
  return String.fromCharCode(65 + c1) + String.fromCharCode(65 + c2);
}

function getFlagUrl(flagItem) {
  if (!flagItem) return null;
  if (flagItem.image_url) return flagItem.image_url;
  // 1) Try the flag emoji in icon (e.g. "🇮🇳" → "IN" → flagcdn/in.png)
  const code = flagEmojiToCode(flagItem.icon);
  if (code) return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
  // 2) Fallback: parse the name
  const name = (flagItem.name || '').toLowerCase();
  for (const [country, c] of Object.entries(FLAG_CODES)) {
    if (name.includes(country)) return `https://flagcdn.com/w40/${c}.png`;
  }
  return null;
}

// Cache for equipped items per user (avoids re-fetching every render)
const _equippedCache = {};
const _stickersCache = {};

// v225 patch: pub/sub so mounted avatars + sticker consumers re-render on cache change
const _subscribers = new Set();
function _notify(userId) {
  _subscribers.forEach(fn => { try { fn(userId); } catch(_){} });
}

// Hook: subscribe to equipped/sticker cache for a userId.
// Re-renders whenever invalidateCache(userId) or updateCache(userId,...) is called.
export function useEquippedItems(userId) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!userId) return undefined;
    const cb = (changedId) => { if (changedId === userId) force(v => v + 1); };
    _subscribers.add(cb);
    return () => { _subscribers.delete(cb); };
  }, [userId]);
  return {
    equipped: _equippedCache[userId] || null,
    stickers: _stickersCache[userId] || [],
  };
}

function UserAvatar({ userId, username, equipped: equippedProp, size = 36, showFlag = true, showStickers = false }) {
  const [equipped, setEquipped] = useState(equippedProp || _equippedCache[userId] || null);
  const [stickers, setStickers] = useState(_stickersCache[userId] || []);

  // Fetch equipped items if not provided as prop
  useEffect(() => {
    if (equippedProp) { setEquipped(equippedProp); return undefined; }
    if (!userId) return undefined;

    let alive = true;
    const fetchProfile = () => {
      const tok = localStorage.getItem('pc_token');
      if (!tok) return;
      fetch(`${(window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : 'https://api.papercrypto.app/api')}/profile/${userId}`, {
        headers: { Authorization: `Bearer ${tok}` }
      })
        .then(r => r.json())
        .then(d => {
          if (!alive || !d.success) return;
          if (d.equipped) { _equippedCache[userId] = d.equipped; setEquipped(d.equipped); }
          if (d.stickers) { _stickersCache[userId] = d.stickers; setStickers(d.stickers); }
          _notify(userId);
        })
        .catch(() => {});
    };

    // Use cache if present, else fetch
    if (_equippedCache[userId]) {
      setEquipped(_equippedCache[userId]);
      setStickers(_stickersCache[userId] || []);
    } else {
      fetchProfile();
    }

    // Subscribe so this instance re-fetches when cache is invalidated elsewhere
    const cb = (changedId) => {
      if (changedId !== userId) return;
      if (_equippedCache[userId]) {
        setEquipped(_equippedCache[userId]);
        setStickers(_stickersCache[userId] || []);
      } else {
        fetchProfile();
      }
    };
    _subscribers.add(cb);
    return () => { alive = false; _subscribers.delete(cb); };
  }, [userId, equippedProp]);

  const persona = equipped?.personas;
  const frame   = equipped?.auras;        // v226: canonical category — `frames` deactivated
  const flag    = equipped?.nation_tags;  // v226: canonical category — `flags` deactivated
  const frameCol = frame ? rarityColor(frame.rarity) : G;
  const glow     = frame ? rarityGlow(frame.rarity) : 'none';
  const hasFrameImg = frame?.image_url;
  const frameSize = size + Math.max(8, Math.floor(size * 0.14));
  const flagSize = Math.max(14, Math.floor(size * 0.34));

  // v265 (G4.9-MOBILE-USERAVATAR): Smart border scaling — thicker borders on
  // larger (profile-size) avatars match the old ComposedAvatar visual at size=90,
  // thinner borders on small (list/header) sizes. Lets ONE component render
  // correctly at every size from 24 to 120+. ComposedAvatar deleted in this session.
  const borderWidth   = size >= 80 ? 3 : 2;
  const frameInsetAbs = size >= 80 ? 4 : 3;

  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
      {/* Aura glow keyframes */}
      <style>{`@keyframes auraGlow{from{box-shadow:0 0 8px #F5C51844,0 0 20px #F5C51822}to{box-shadow:0 0 16px #F5C51888,0 0 40px #F5C51844}}`}</style>
      {/* Aura Frame */}
      {hasFrameImg ? (
        // S-031 FIX: Aura image was offset by -size*0.07 with frameSize=size+0.14*size
        // creating visible gap between avatar circle and aura ring. Fixed by:
        //   • Using SAME inset as the non-image fallback (-frameInsetAbs)
        //   • Sizing the aura image to size+2*frameInsetAbs (snug fit)
        //   • Aura images render BEHIND avatar (zIndex 1, avatar 2)
        // P34c (2026-05-24): Resolve relative /store URLs through backend origin
        // (production architecture — backend owns store assets, not webapp/public/)
        <img src={toAbsoluteStoreUrl(frame.image_url)} alt=""
          style={{ position:'absolute', inset: -frameInsetAbs,
            width: size + 2*frameInsetAbs, height: size + 2*frameInsetAbs,
            borderRadius:'50%', zIndex:1, pointerEvents:'none',
            filter: glow !== 'none' ? `drop-shadow(${glow})` : 'none',
            animation: frame?.is_animated ? 'auraGlow 2s ease-in-out infinite alternate' : 'none' }}
          loading="lazy"
          decoding="async"/>
      ) : (
        <div style={{ position:'absolute', inset:-frameInsetAbs, borderRadius:'50%',
          border:`${borderWidth}px solid ${frameCol}`, boxShadow:glow, zIndex:1, pointerEvents:'none',
          animation: frame?.is_animated ? 'auraGlow 2s ease-in-out infinite alternate' : 'none' }}/>
      )}

      {/* Avatar Circle — persona image / icon / initial (v226 bug fix: render image_url for DiceBear personas) */}
      <div style={{ width:size, height:size, borderRadius:'50%', overflow:'hidden',
        background:`linear-gradient(135deg,#C9960C,${G})`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:Math.floor(size*0.44), fontWeight:800, color:'#0D0A00', userSelect:'none' }}>
        {persona && persona.image_url
          ? <img src={toAbsoluteStoreUrl(persona.image_url)} alt={persona.name || 'persona'}
              style={{ width:'100%', height:'100%', objectFit:'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }}/>
          : persona
            ? <span style={{ fontSize:Math.floor(size*0.5) }}>{persona.icon || '🦸'}</span>
            : <span>{username?.[0]?.toUpperCase() || '?'}</span>}
      </div>

      {/* Nation Tag Badge */}
      {showFlag && flag && (
        <div style={{ position:'absolute', bottom:-1, right:-1, zIndex:3,
          width:flagSize, height:flagSize, borderRadius:'50%',
          background:'#1A1200', border:`1.5px solid ${rarityColor(flag.rarity, G)}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:Math.floor(flagSize*0.6), overflow:'hidden' }}>
          {(() => {
            const flagUrl = getFlagUrl(flag);
            if (flagUrl) return <img src={flagUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }}
      loading="lazy"
      decoding="async"/>;
            return flag.icon || '🏳️';
          })()}
        </div>
      )}

      {/* v225/v226 Stickers — top-right cluster, only when showStickers=true. Render image_url (Fluent 3D) when present, else fall back to icon */}
      {showStickers && stickers && stickers.length > 0 && (
        <div style={{ position:'absolute', top:-Math.floor(size*0.15), right:-Math.floor(size*0.15),
          zIndex:4, display:'flex', gap:1, pointerEvents:'none' }}>
          {stickers.slice(0, 3).map((st, i) => {
            const stickerSize = Math.max(12, Math.floor(size * 0.32));
            return st.image_url
              ? <img key={st.item_id || i} src={st.image_url} alt={st.name || ''}
                  style={{ width:stickerSize, height:stickerSize, objectFit:'contain',
                    filter:'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }}
                  onError={(e) => { e.target.style.display = 'none'; }}/>
              : <span key={st.item_id || i} style={{ fontSize:stickerSize, lineHeight:1,
                  filter:'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }}>
                  {st.icon || '⭐'}
                </span>;
          })}
        </div>
      )}
    </div>
  );
}

UserAvatar.invalidateCache = (userId) => {
  delete _equippedCache[userId];
  delete _stickersCache[userId];
  _notify(userId);
};
UserAvatar.updateCache = (userId, equipped) => {
  _equippedCache[userId] = equipped;
  _notify(userId);
};
UserAvatar.getStickers = (userId) => _stickersCache[userId] || [];

// v226 BUG FIX: React.memo() wraps the function in a new object, so static
// methods attached to UserAvatar above were NOT visible on the default export.
// External callers doing `require('./UserAvatar').default.invalidateCache()`
// got `undefined` (silently caught by their try/catch) — meaning Sidebar/
// Leaderboard/Forums NEVER got cache-invalidation events on equip/unequip.
// Fix: re-attach the same static methods to the memoized wrapper.
const MemoizedUserAvatar = React.memo(UserAvatar);
MemoizedUserAvatar.invalidateCache = UserAvatar.invalidateCache;
MemoizedUserAvatar.updateCache     = UserAvatar.updateCache;
MemoizedUserAvatar.getStickers     = UserAvatar.getStickers;

export default MemoizedUserAvatar;
